/**
 * Pigment palette — the only source of colour (art-direction §2). Owner: Engine (F2).
 *
 * - §2.1 pigment tokens (hex sRGB), §2.5 fur palettes, §2.7 irises, §2.8 dyes and tinctures, §2.12 edition palettes;
 * - colour math (sRGB ↔ linear ↔ OKLab ↔ OKLCH), WCAG contrast;
 * - §2.3 `deriveRamp` (the Cennini three-tone rule) and the scene shadow tints;
 * - the art §3.2 `CreatureLook` host layout (528 B) and `packCreatureLook` (pure; used by the creature look builder and
 *   by the engine's test meshes);
 * - `tokensCss()` — the generator of `src/ui/tokens.css` (art §2.11; F3 writes the file from this string).
 *
 * Pure data + math: no DOM, no GPU.
 */

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §2.1 Pigment tokens
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export const TOKENS = {
  inkDeep: '#17110D', ink: '#2B1D14', inkFaded: '#5A4632', leadWhite: '#F7F1E1', vellumLight: '#FBF5E4',
  vellum: '#F1E4C3', vellumAged: '#E6D3A3', vellumShadow: '#CDB37E', char: '#3A2414', lapisNight: '#0F1C4D',
  lapis: '#1B2F7A', lapisLight: '#2747A8', azurite: '#3E64B0', lapisPale: '#6F86C8', vermilion: '#C8341E',
  rubric: '#A82A17', minium: '#E0692E', madder: '#C76B7E', bole: '#8E3A22', sinopia: '#9A4A2E', goldDeep: '#9C7424',
  gold: '#D4A537', goldLight: '#F3D77A', glint: '#FFF4C2', shellGold: '#C29A45', copperLeaf: '#B5612B',
  verdigrisDeep: '#24594A', verdigris: '#3C8C74', verdigrisLight: '#5FB096', terreVerte: '#8A9A6B', verdaccio: '#7A8160',
  ochre: '#C9A04A', ochreLight: '#D8B98A', umber: '#5C3A22', terracotta: '#A65A3A', folium: '#7A3B66', soot: '#2E2A2A',
  ironGray: '#6E7078', intonaco: '#EBDDC6', foxing: '#B98A4E',
} as const;
export type TokenId = keyof typeof TOKENS;
export const TOKEN_IDS = Object.keys(TOKENS) as TokenId[];

/** Russian pigment names (player-facing in fur / dye pickers). */
export const TOKEN_NAMES_RU: Record<TokenId, string> = {
  inkDeep: 'Чернильная гуща', ink: 'Железо-галловые чернила', inkFaded: 'Выцветшие чернила', leadWhite: 'Свинцовые белила',
  vellumLight: 'Пергамент светлый', vellum: 'Пергамент', vellumAged: 'Старый пергамент', vellumShadow: 'Тень пергамента',
  char: 'Гарь', lapisNight: 'Ночная лазурь', lapis: 'Лазурит', lapisLight: 'Лазурь светлая', azurite: 'Азурит',
  lapisPale: 'Лазурь с белилами', vermilion: 'Киноварь', rubric: 'Рубрика', minium: 'Сурик', madder: 'Марена',
  bole: 'Болюс', sinopia: 'Синопия', goldDeep: 'Золото в тени', gold: 'Сусальное золото', goldLight: 'Золото на свету',
  glint: 'Отблеск', shellGold: 'Твореное золото', copperLeaf: 'Медь листовая', verdigrisDeep: 'Ярь тёмная',
  verdigris: 'Ярь-медянка', verdigrisLight: 'Ярь светлая', terreVerte: 'Зелёная земля', verdaccio: 'Вердаччо',
  ochre: 'Охра', ochreLight: 'Охра светлая', umber: 'Умбра', terracotta: 'Терракота', folium: 'Фолиум', soot: 'Сажа',
  ironGray: 'Потемневшее серебро', intonaco: 'Штукатурка', foxing: 'Рыжие пятна',
};

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Colour math
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export type Rgb = [number, number, number];
export type Lab = [number, number, number];

/** '#RRGGBB' → sRGB 0..1. */
export function hexToRgb(hex: string): Rgb {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) throw new Error(`palette: bad hex ${hex}`);
  const n = Number.parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
/** sRGB 0..1 → '#RRGGBB' (clamped, rounded). */
export function rgbToHex(c: Rgb): string {
  const h = (v: number): string => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0');
  return `#${h(c[0])}${h(c[1])}${h(c[2])}`.toUpperCase();
}
export const srgbToLinear1 = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
export const linearToSrgb1 = (c: number): number => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
export const srgbToLinear = (c: Rgb): Rgb => [srgbToLinear1(c[0]), srgbToLinear1(c[1]), srgbToLinear1(c[2])];
export const linearToSrgb = (c: Rgb): Rgb => [linearToSrgb1(c[0]), linearToSrgb1(c[1]), linearToSrgb1(c[2])];
/** '#RRGGBB' → linear RGB (what every uniform carries). */
export const hexToLinear = (hex: string): Rgb => srgbToLinear(hexToRgb(hex));

/** Linear sRGB → OKLab (Björn Ottosson). */
export function linearToOklab(c: Rgb): Lab {
  const l = Math.cbrt(0.4122214708 * c[0] + 0.5363325363 * c[1] + 0.0514459929 * c[2]);
  const m = Math.cbrt(0.2119034982 * c[0] + 0.6806995451 * c[1] + 0.1073969566 * c[2]);
  const s = Math.cbrt(0.0883024619 * c[0] + 0.2817188376 * c[1] + 0.6299787005 * c[2]);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}
/** OKLab → linear sRGB (unclamped). */
export function oklabToLinear(lab: Lab): Rgb {
  const l = (lab[0] + 0.3963377774 * lab[1] + 0.2158037573 * lab[2]) ** 3;
  const m = (lab[0] - 0.1055613458 * lab[1] - 0.0638541728 * lab[2]) ** 3;
  const s = (lab[0] - 0.0894841775 * lab[1] - 1.291485548 * lab[2]) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}
export const hexToOklab = (hex: string): Lab => linearToOklab(hexToLinear(hex));
/** OKLCH = (L, C, h°). */
export function oklabToOklch(lab: Lab): [number, number, number] {
  const C = Math.hypot(lab[1], lab[2]);
  let h = (Math.atan2(lab[2], lab[1]) * 180) / Math.PI;
  if (h < 0) h += 360;
  return [lab[0], C, h];
}
export const hexToOklch = (hex: string): [number, number, number] => oklabToOklch(hexToOklab(hex));
/** OKLab Euclidean ΔE. */
export const deltaE = (a: Lab, b: Lab): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
export const clampRgb = (c: Rgb): Rgb => [clamp01(c[0]), clamp01(c[1]), clamp01(c[2])];

/** WCAG 2.x relative luminance of an sRGB hex. */
export function luminance(hex: string): number {
  const c = hexToLinear(hex);
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
/** WCAG 2.x contrast ratio (≥ 1). */
export function contrast(fg: string, bg: string): number {
  const a = luminance(fg), b = luminance(bg);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §2.3 Shading ramps — the Cennini three-tone rule
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Light / mid / shadow, linear RGB (clamped 0..1). */
export interface Ramp { light: Rgb; mid: Rgb; shadow: Rgb }
export interface RampOptions {
  /** Scene shadow tint (hex) and its strength k (default 0.22). */
  shadowTint: string; k?: number;
  /** Flesh: verdaccio k 0.40 (§2.3). Overrides the scene tint. */
  overrideTint?: { tint: string; k: number };
  /** Dungeon raises the dL clamp to 0.20. */
  dLMax?: number;
}

/** Scene shadow tints (§2.3). */
export const SCENE_TINTS = {
  arena: { tint: '#26306B', k: 0.22 },
  constructor: { tint: '#4A4A5A', k: 0.12 },
  dungeon: { tint: TOKENS.lapisNight, k: 0.4, dLMax: 0.2 },
  map: { tint: '#8A6A3F', k: 0.15 },
  cardMini: { tint: '#4A4A5A', k: 0.12 },
  diaper: { tint: '#4A2C3A', k: 0.18 },
  goldGround: { tint: TOKENS.umber, k: 0.25 },
} as const;
export type SceneTintId = keyof typeof SCENE_TINTS;
/** Flesh override (§2.3: verdaccio, k = 0.40). */
export const FLESH_TINT = { tint: TOKENS.verdaccio, k: 0.4 } as const;

/** §2.3 `deriveRamp` in OKLab → linear sRGB. */
export function deriveRamp(midHex: string, opts: RampOptions): Ramp {
  const mid = hexToOklab(midHex);
  const white = hexToOklab(TOKENS.leadWhite);
  const light: Lab = [mid[0] + (white[0] - mid[0]) * 0.28, mid[1] + (white[1] - mid[1]) * 0.28, mid[2] + (white[2] - mid[2]) * 0.28];
  const dL = Math.min(opts.dLMax ?? 0.16, Math.max(0.07, 0.26 * mid[0]));
  const t = opts.overrideTint ? hexToOklab(opts.overrideTint.tint) : hexToOklab(opts.shadowTint);
  const k = opts.overrideTint ? opts.overrideTint.k : (opts.k ?? 0.22);
  const shadow: Lab = [mid[0] - dL, 0.95 * mid[1] + (t[1] - 0.95 * mid[1]) * k, 0.95 * mid[2] + (t[2] - 0.95 * mid[2]) * k];
  return { light: clampRgb(oklabToLinear(light)), mid: clampRgb(oklabToLinear(mid)), shadow: clampRgb(oklabToLinear(shadow)) };
}
/** Convenience: ramp in a named scene. */
export function sceneRamp(midHex: string, scene: SceneTintId, flesh = false): Ramp {
  const s = SCENE_TINTS[scene] as { tint: string; k: number; dLMax?: number };
  return deriveRamp(midHex, { shadowTint: s.tint, k: s.k, dLMax: s.dLMax, overrideTint: flesh ? FLESH_TINT : undefined });
}
/** Ramp as hex triple (tests, debug). */
export function rampHex(r: Ramp): { light: string; mid: string; shadow: string } {
  return { light: rgbToHex(linearToSrgb(r.light)), mid: rgbToHex(linearToSrgb(r.mid)), shadow: rgbToHex(linearToSrgb(r.shadow)) };
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §2.4–§2.8 Flesh, fur palettes, patterns, eyes, dyes, tinctures
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export const FLESH = { noseLight: '#D98A8A', noseDark: '#4A3434', innerEar: '#E3A6A0', carnation: '#D9A383' } as const;

export interface FurPalette { id: string; ru: string; base: string; stripe: string; stripe2?: string; belly: string; stroke: string; cov: number; gilded?: boolean }
export const FUR_PALETTES: readonly FurPalette[] = [
  { id: 'ginger', ru: 'Рыжий', base: '#D27A34', stripe: '#9E4A1C', belly: '#F2D3A2', stroke: '#5C2A12', cov: 0.45 },
  { id: 'greyTabby', ru: 'Сизый', base: '#8C8478', stripe: '#4E4740', belly: '#D8D0C0', stroke: '#2B1D14', cov: 0.5 },
  { id: 'marginBlue', ru: 'Чернильный', base: '#6F86C2', stripe: '#34477F', belly: '#C9D3EC', stroke: '#1B2F7A', cov: 0.55 },
  { id: 'whiteFur', ru: 'Белёсый', base: '#EDE6D6', stripe: '#BDB4A0', belly: '#FBF6EA', stroke: '#6B5E4E', cov: 0.8 },
  { id: 'sootFur', ru: 'Сажа', base: '#2E2A2A', stripe: '#151212', belly: '#4A4340', stroke: '#6B6158', cov: 0.6 },
  { id: 'ochreFur', ru: 'Охряный', base: '#C9A04A', stripe: '#8A6A28', belly: '#EFD9A0', stroke: '#5C3A22', cov: 0.5 },
  { id: 'umberFur', ru: 'Бурый', base: '#7A5235', stripe: '#4A2F1D', belly: '#C9A47E', stroke: '#2B1D14', cov: 0.45 },
  { id: 'tortie', ru: 'Черепаховый', base: '#3B2A22', stripe: '#C7672A', belly: '#E8C690', stroke: '#17110D', cov: 0.4 },
  { id: 'calicoFur', ru: 'Трёхшёрстный', base: '#F1E8D6', stripe: '#D07A35', stripe2: '#2E2A2A', belly: '#FBF6EA', stroke: '#5A4632', cov: 0.4 },
  { id: 'siamese', ru: 'Сиамский', base: '#E9DCC4', stripe: '#5B3E2E', belly: '#F6EEDD', stroke: '#3A2A20', cov: 0.35 },
  { id: 'verdigrisFur', ru: 'Ярь-медянка', base: '#5FA58C', stripe: '#2E6B58', belly: '#CFE6D8', stroke: '#1E4238', cov: 0.5 },
  { id: 'vermilionFur', ru: 'Киноварный', base: '#C8442A', stripe: '#7E1E12', belly: '#F0B48C', stroke: '#3E0E08', cov: 0.5 },
  { id: 'goldLeafFur', ru: 'Золочёный', base: TOKENS.gold, stripe: TOKENS.goldDeep, belly: TOKENS.goldLight, stroke: TOKENS.umber, cov: 0.3, gilded: true },
  { id: 'sketch', ru: 'Набросок', base: TOKENS.vellum, stripe: TOKENS.ink, belly: TOKENS.vellumLight, stroke: TOKENS.ink, cov: 0.7 },
];
export const furPalette = (id: string): FurPalette => FUR_PALETTES.find((p) => p.id === id) ?? FUR_PALETTES[1];

/** §2.5 species defaults (base / stripe / belly). */
export const SPECIES_FUR = {
  hare: { base: '#B08A5E', stripe: '#7A5A3A', belly: '#EADBC0' },
  fox: { base: '#C8642A', stripe: '#7E3A18', belly: '#F2E2C8' },
  rat: { base: '#8A8078', stripe: '#5A524C', belly: '#D8D0C0' },
  goat: { base: '#D8CCB4', stripe: '#A89C84', belly: '#F1E8D6' },
  bear: { base: '#6B4A30', stripe: '#4A2F1D', belly: '#C9A47E' },
  snail: { shell: '#B9925A', spiral: '#7A5A3A', body: '#A8B39A' },
  toad: { base: TOKENS.terreVerte, stripe: TOKENS.verdigrisDeep, belly: TOKENS.ochreLight },
} as const;

/** §2.6 pattern ids → `CreatureLook.pattern.x`. */
export const PATTERNS = ['solid', 'mackerel', 'classic', 'spotted', 'ticked', 'bicolor', 'point', 'calico', 'ermine', 'vair'] as const;
export type PatternId = (typeof PATTERNS)[number];
export const patternIndex = (id: PatternId): number => PATTERNS.indexOf(id);

/** §2.7 irises. */
export const IRISES = { amber: '#C99A2E', honey: '#D9B45A', verdigris: '#5FA58C', lapis: '#3E64B0', copper: '#B5612B', void: '#2B1D14', milky: '#DAD6CC' } as const;
export type IrisId = keyof typeof IRISES;

/** §2.8 cloth dyes (RU name → mid). */
export const DYES = {
  vermilion: { ru: 'Киноварь', hex: '#C8341E' }, lapis: { ru: 'Лазурь', hex: '#2747A8' }, verdigris: { ru: 'Ярь', hex: '#3C8C74' },
  folium: { ru: 'Фолиум', hex: '#7A3B66' }, ochre: { ru: 'Охра', hex: '#C9A04A' }, leadWhite: { ru: 'Белила', hex: '#F7F1E1' },
  soot: { ru: 'Сажа', hex: '#2E2A2A' }, madder: { ru: 'Марена', hex: '#C76B7E' }, saffron: { ru: 'Шафран', hex: '#E3B23C' },
  woad: { ru: 'Вайда', hex: '#4F6A8F' },
} as const;
export type DyeId = keyof typeof DYES;

/** §2.8 heraldic tinctures (rule of tincture: never colour on colour, never metal on metal). */
export const TINCTURES = {
  or: { kind: 'metal', token: 'gold' }, argent: { kind: 'metal', token: 'leadWhite' }, gules: { kind: 'color', token: 'vermilion' },
  azure: { kind: 'color', token: 'lapisLight' }, vert: { kind: 'color', token: 'verdigris' }, purpure: { kind: 'color', token: 'folium' },
  sable: { kind: 'color', token: 'soot' },
} as const satisfies Record<string, { kind: 'metal' | 'color'; token: TokenId }>;
export type TinctureId = keyof typeof TINCTURES;
/** Rule of tincture: a charge may sit on a field only if one is metal and the other colour. */
export const tinctureOk = (field: TinctureId, charge: TinctureId): boolean => TINCTURES[field].kind !== TINCTURES[charge].kind;

/** §2.2 contrast pairs (fg, bg, ratio of record). */
export const CONTRAST_PAIRS: ReadonlyArray<[TokenId, TokenId, number]> = [
  ['ink', 'vellum', 12.9], ['ink', 'vellumAged', 11.0], ['inkFaded', 'vellum', 7.1], ['rubric', 'vellum', 5.5],
  ['vermilion', 'vellum', 4.2], ['lapis', 'vellum', 9.6], ['folium', 'vellum', 6.3], ['verdigrisDeep', 'vellum', 6.4],
  ['verdigris', 'vellum', 3.2], ['leadWhite', 'vermilion', 4.7], ['leadWhite', 'rubric', 6.2], ['leadWhite', 'lapis', 10.7],
  ['leadWhite', 'lapisLight', 7.3], ['ink', 'gold', 7.2], ['gold', 'lapisNight', 7.1], ['goldLight', 'lapis', 8.5], ['gold', 'vellum', 1.8],
];

/** §2.12 edition / night palettes — parameter sets, not new pigments. */
export const EDITION_PALETTES = {
  edition1: { mapInk: 'ink', goldHairline: false, washChromaGain: 0, gildingSheen: 1, monsterRim: null, keylineToken: 'leadWhite' },
  edition2: { mapInk: 'umber', goldHairline: true, washChromaGain: 0.1, gildingSheen: 1.5, monsterRim: { token: 'gold', strength: 0.6 }, keylineToken: 'goldLight' },
  night: { borderWash: { token: 'lapisNight', alpha: 0.35 }, townWindows: 'minium', waterLines: 'goldLight', exposure: 0.94 },
} as const;
/** Max OKLCH chroma allowed anywhere (§2.1 "Forbidden"). */
export const MAX_CHROMA = 0.19;

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Material ids (art §3.2) and region ids
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export const MAT = {
  fur: 0, flesh: 1, sclera: 2, iris: 3, pupil: 4, dye0: 5, dye1: 6, dye2: 7, dye3: 8, leather: 9, wood: 10, iron: 11,
  gold: 12, gem: 13, parchment: 14, stone: 15, horn: 16, plumage: 17, shell: 18, copper: 19, silver: 20,
} as const;
export type MaterialId = (typeof MAT)[keyof typeof MAT];
export const REGION = { skull: 0, muzzle: 1, ears: 2, neck: 3, torso: 4, upperArms: 5, forearms: 6, thighs: 7, shins: 8, tail: 9, extras: 10 } as const;

/** Fixed material mid tones (§4.2-D). */
export const MATERIAL_MIDS = {
  wood: '#9A6B3F', leather: '#7A4E2C', stone: '#B8AC92', horn: TOKENS.leadWhite, shell: '#B9925A', iron: TOKENS.ironGray,
} as const;

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// art §3.2 CreatureLook host layout (528 B = 132 floats; d.sizeOf-verified in cheatsheet §3.10)
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Float offsets of every `CreatureLook` member. `pattern` is vec4u (written through a Uint32Array view). */
export const LOOK = {
  rampBase: 0, rampStripe: 12, rampBelly: 24, rampFlesh: 36, rampDye: 48, strokeInk: 96, iris: 100, pattern: 104,
  patParams: 108, headFrame: 112, line: 116, fx: 120, face: 124, fxColor: 128,
} as const;
export const LOOK_FLOATS = 132;
export const LOOK_BYTES = LOOK_FLOATS * 4;
/** `pattern.z` flag bits (art §3.2). */
export const LOOK_FLAG = { tabbyM: 1, catchlight: 2, keyline: 4, mirrored: 8, gilded: 16 } as const;

/** Everything a CreatureLook needs, in pigment terms (the DNA → look mapping is the creature module's). */
export interface LookSpec {
  fur: FurPalette;
  pattern: PatternId;
  /** Up to 4 dye slot mids (hex); missing slots repeat the last. */
  dyes: string[];
  iris: string;
  /** Flesh mid (nose, pads, inner ear, carnation). */
  flesh?: string;
  scene?: SceneTintId;
  seed?: number;
  /** freq /H, warp, threshold, bellyWidth (§2.6 defaults per pattern when omitted). */
  patParams?: [number, number, number, number];
  /** Rest head centre (H) + radius (forehead M, §2.6). */
  headFrame?: [number, number, number, number];
  /** silhouettePx, creasePx, wobbleAmp, shadowSideGain (§4.3). */
  line?: [number, number, number, number];
  flags?: number;
}

const PATTERN_DEFAULTS: Record<PatternId, [number, number, number, number]> = {
  solid: [0, 0, 0, 0.13], mackerel: [9, 0.35, 0.62, 0.13], classic: [7, 0.5, 0.6, 0.13], spotted: [14, 0.3, 0.28, 0.13],
  ticked: [0, 0, 0, 0.13], bicolor: [0, 0, 0, 0.13], point: [0, 0, 0, 0.13], calico: [5, 0.4, 0.5, 0.13], ermine: [8, 0, 0, 0.13], vair: [8, 0, 0, 0.13],
};

function writeRamp(out: Float32Array, at: number, r: Ramp): void {
  out[at] = r.light[0]; out[at + 1] = r.light[1]; out[at + 2] = r.light[2]; out[at + 3] = 1;
  out[at + 4] = r.mid[0]; out[at + 5] = r.mid[1]; out[at + 6] = r.mid[2]; out[at + 7] = 1;
  out[at + 8] = r.shadow[0]; out[at + 9] = r.shadow[1]; out[at + 10] = r.shadow[2]; out[at + 11] = 1;
}

/** Packs a `CreatureLook` (art §3.2) into a 528 B buffer (linear colours, ramps light/mid/shadow). Pure. */
export function packCreatureLook(spec: LookSpec, out: ArrayBuffer = new ArrayBuffer(LOOK_BYTES)): ArrayBuffer {
  const f = new Float32Array(out, 0, LOOK_FLOATS);
  const u = new Uint32Array(out, 0, LOOK_FLOATS);
  const scene = spec.scene ?? 'arena';
  writeRamp(f, LOOK.rampBase, sceneRamp(spec.fur.base, scene));
  writeRamp(f, LOOK.rampStripe, sceneRamp(spec.fur.stripe, scene));
  writeRamp(f, LOOK.rampBelly, sceneRamp(spec.fur.belly, scene));
  writeRamp(f, LOOK.rampFlesh, sceneRamp(spec.flesh ?? FLESH.noseLight, scene, true));
  for (let i = 0; i < 4; i++) {
    const hex = spec.dyes[Math.min(i, spec.dyes.length - 1)] ?? TOKENS.vermilion;
    writeRamp(f, LOOK.rampDye + i * 12, sceneRamp(hex, scene));
  }
  const ink = hexToLinear(spec.fur.stroke);
  f[LOOK.strokeInk] = ink[0]; f[LOOK.strokeInk + 1] = ink[1]; f[LOOK.strokeInk + 2] = ink[2]; f[LOOK.strokeInk + 3] = spec.fur.cov;
  const iris = hexToLinear(spec.iris);
  f[LOOK.iris] = iris[0]; f[LOOK.iris + 1] = iris[1]; f[LOOK.iris + 2] = iris[2]; f[LOOK.iris + 3] = 1;
  const isTabby = spec.pattern === 'mackerel' || spec.pattern === 'classic';
  u[LOOK.pattern] = patternIndex(spec.pattern);
  u[LOOK.pattern + 1] = (spec.seed ?? 1) >>> 0;
  u[LOOK.pattern + 2] = (spec.flags ?? 0) | (isTabby ? LOOK_FLAG.tabbyM : 0) | (spec.fur.gilded ? LOOK_FLAG.gilded : 0);
  u[LOOK.pattern + 3] = 0;
  f.set(spec.patParams ?? PATTERN_DEFAULTS[spec.pattern], LOOK.patParams);
  f.set(spec.headFrame ?? [0, 0.845, 0.04, 0.16], LOOK.headFrame);
  f.set(spec.line ?? [2.0, 1.25, 0.25, 1.0], LOOK.line);
  f.set([0, 1, 0, 0], LOOK.fx);
  f.set([1, 1, 0, 0], LOOK.face);
  f.set([0, 0, 0, 0], LOOK.fxColor);
  return out;
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §2.11 tokens.css generator
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

const kebab = (s: string): string => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
/** Short aliases used by the §2.11 excerpt. */
const CSS_ALIASES: Partial<Record<TokenId, string>> = { copperLeaf: 'copper' };

/** The full `src/ui/tokens.css` text: every §2.1 token, the §2.11 aliases and the bake slots (flat until baked). */
export function tokensCss(): string {
  const lines: string[] = [];
  for (const id of TOKEN_IDS) {
    lines.push(`  --mc-${kebab(id)}:${TOKENS[id]};`);
    const alias = CSS_ALIASES[id];
    if (alias) lines.push(`  --mc-${alias}:${TOKENS[id]};`);
  }
  lines.push('  --mc-line:1.5px; --mc-radius:4px; --mc-tap:48px;');
  lines.push('  --mc-vellum-tex:none; --mc-gold-tex:none; --mc-gold-sheen:none; --mc-lapis-tex:none; --mc-vermilion-tex:none;');
  return `/* Generated from src/content/palette.ts (art-direction §2.11). Do not edit by hand. */\n:root {\n${lines.join('\n')}\n}\n`;
}

/** Token map for `__marg.render.palette()`: hex + linear + OKLCH. */
export function paletteTable(): Record<TokenId, { hex: string; linear: Rgb; oklch: [number, number, number] }> {
  const out = {} as Record<TokenId, { hex: string; linear: Rgb; oklch: [number, number, number] }>;
  for (const id of TOKEN_IDS) out[id] = { hex: TOKENS[id], linear: hexToLinear(TOKENS[id]), oklch: hexToOklch(TOKENS[id]) };
  return out;
}
